import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatProjectNumber, type ProjectLinkable } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { idem } from '../../lib/idempotency';
import { SectionCard } from '../ui/Detail';
import {
  StaleNotice,
  UnresolvedState,
  refreshFailed,
  shownState,
} from '../ui/QueryStates';
import { IconProjects } from '../icons';
import { Notice } from '../ui/Badge';
import { field } from '../create/Field';

/**
 * "Is this part of a build?" (PRD v2 §6.8) — one select, wherever a piece of work lives.
 *
 * ONE component for jobs, quotes, invoices and purchase orders, because it is one act and
 * four copies would be four slightly different answers within a month. The control lives on
 * the WORK rather than on the project: somebody books a job and knows what it belongs to,
 * whereas going the other way means opening the project and hunting for a number.
 *
 * For the residential shop that never opens a project, this renders one sentence and
 * nothing to do — which is the whole promise of making the association optional.
 */
export function ProjectLinkCard({
  entityType,
  entityId,
  customerId,
  projectId,
  disabled = false,
}: {
  entityType: ProjectLinkable;
  entityId: string;
  /**
   * Scopes the options to one customer's builds. Omitted for a purchase order, which has
   * a vendor but no customer — there, every open project is a legitimate destination.
   */
  customerId?: string;
  /** What it's on now, from the parent's already-loaded record. */
  projectId: string | null;
  /** Parent lifecycle can make its header immutable; other work keeps the existing flow. */
  disabled?: boolean;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const options = useQuery(
    trpc.project.picker.queryOptions(customerId ? { customerId } : {}),
  );

  const link = useMutation(
    trpc.project.link.mutationOptions({
      onSuccess: () => void qc.invalidateQueries(),
    }),
  );

  // "This customer has no open projects" is a claim about the server, and a query that has
  // not answered has not made it.
  const state = shownState(options, options.data);
  const list = options.data ?? [];
  const current = list.find((p) => p.id === projectId);
  // A closed project won't appear in the picker, so a link to one must still be reachable.
  const orphanLink = projectId && !current;

  return (
    <SectionCard title="Project">
      <StaleNotice
        show={refreshFailed(options, options.data)}
        onRetry={() => void options.refetch()}
      />
      {link.error ? <Notice tone="danger">{link.error.message}</Notice> : null}

      {state !== 'ready' ? (
        <UnresolvedState
          state={state}
          what="this customer's projects"
          icon={IconProjects}
          error={options.error}
          onRetry={() => void options.refetch()}
          skeleton="h-10"
        />
      ) : list.length === 0 && !projectId ? (
        <p className="text-sm text-muted-foreground">
          {customerId ? 'This customer has no open projects.' : 'No open projects yet.'}{' '}
          Start one from{' '}
          <Link to="/projects" className="underline">
            Projects
          </Link>{' '}
          when a build has several work orders worth tracking together.
        </p>
      ) : (
        <>
          <select
            className={field}
            aria-label="Project this belongs to"
            value={projectId ?? ''}
            disabled={disabled || link.isPending}
            onChange={(e) =>
              link.mutate({
                entityType,
                entityId,
                projectId: e.target.value || null,
                ...idem(),
              })
            }
          >
            <option value="">Not part of a project</option>
            {orphanLink ? <option value={projectId}>On a closed project</option> : null}
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {formatProjectNumber(p.projectNumber)} · {p.name}
              </option>
            ))}
          </select>
          {projectId ? (
            <Link
              to="/projects/$projectId"
              params={{ projectId }}
              className="mt-2 inline-block text-sm underline decoration-dotted"
            >
              {current ? `Open ${current.name}` : 'Open the project'}
            </Link>
          ) : null}
        </>
      )}
    </SectionCard>
  );
}
